/**
 * Phase 29e — PurePay embed bootstrap.
 *
 * On SPA load, if the URL carries `?launch_code=<code>`, exchange it for
 * a real session at `GET /api/v1/auth/federate-launch/{code}`, hydrate
 * localStorage, strip the code from the URL, and let the rest of the app
 * boot normally. Runs OUTSIDE `AuthProvider` so the hydrated token is
 * present before `AuthProvider` calls `/auth/me`.
 *
 * This is iframe-friendly: on failure we render an inline error — never
 * a top-level redirect to `/login` (would break the embedded view).
 *
 * ─── StrictMode safety ──────────────────────────────────────────────
 * React.StrictMode runs effects twice in dev. Launch codes are single-use,
 * so a naïve fetch per effect call burns the code on the first invocation
 * and the second gets 410 `launch_code_consumed`. We dedupe at the module
 * level via a Map<code, Promise<Result>> cache: both effect invocations
 * end up awaiting the SAME in-flight promise, and the first successful
 * result is kept even if an earlier effect was torn down.
 *
 * ─── Router integration ────────────────────────────────────────────
 * Uses `useNavigate()` from react-router-dom to update the URL AND the
 * router state in one step. Raw `history.replaceState` would strip the
 * code but React Router would keep rendering the previous route — the
 * user would see the landing page instead of their dashboard.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, setSession } from "@/lib/api";

const ERROR_COPY = {
  launch_code_invalid: {
    title: "This launch link looks wrong.",
    body: "Your partner app sent a code we don't recognise. Refresh the page from PurePay and try again.",
  },
  launch_code_expired: {
    title: "That link expired.",
    body: "Launch links only stay valid for 60 seconds. Head back to PurePay and open the view again.",
  },
  launch_code_consumed: {
    title: "This link was already used.",
    body: "Launch links are single-use for your safety. Open the view from PurePay to get a fresh one.",
  },
  identity_mismatch: {
    title: "You're not the person this link was for.",
    body: "Ask your admin to re-open this view from your own PurePay account.",
  },
  default: {
    title: "We couldn't hand you off from PurePay.",
    body: "Something broke on the way in. Try opening the view from PurePay again — if it keeps happening, let your admin know.",
  },
};

// Module-level dedupe: one in-flight exchange per code. Survives both
// StrictMode double-mounts AND rapid remount loops in dev tooling. The
// cached promise resolves with { ok, data } — never rejects — so both
// callers follow the same code path.
const inflightExchanges = new Map();

function exchangeLaunchCode(code) {
  if (inflightExchanges.has(code)) {
    return inflightExchanges.get(code);
  }
  const p = (async () => {
    const url = `${API}/v1/auth/federate-launch/${encodeURIComponent(code)}`;
    try {
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      return { ok: resp.ok, status: resp.status, data };
    } catch (_e) {
      return { ok: false, status: 0, data: { detail: { error: "network_error" } } };
    }
  })();
  inflightExchanges.set(code, p);
  return p;
}

function pickLaunchCodeFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const sp = new URLSearchParams(window.location.search);
    const code = sp.get("launch_code");
    return code && code.trim() ? code.trim() : null;
  } catch (_e) {
    return null;
  }
}

function stripLaunchCodeFromUrl(redirectPath) {
  if (typeof window === "undefined") return;
  try {
    const sp = new URLSearchParams(window.location.search);
    sp.delete("launch_code");
    sp.delete("redirect_path");
    const q = sp.toString();
    const base = redirectPath && redirectPath.startsWith("/")
      ? redirectPath
      : (window.location.pathname === "/" ? "/app/dashboard" : window.location.pathname);
    const next = base + (q ? `?${q}` : "") + window.location.hash;
    window.history.replaceState(null, "", next);
  } catch (_e) {
    /* non-fatal — URL stays as-is */
  }
}

// Alias deprecated-but-still-sent partner redirects to their current route.
// Keep this list short — if a partner relied on an old path we never had,
// adding it here is strictly better than a 404 on hand-off.
const REDIRECT_PATH_ALIASES = {
  "/dashboard": "/app/dashboard",
  "/": "/app/dashboard",
  "": "/app/dashboard",
};

function normalizeRedirectPath(path) {
  if (!path || typeof path !== "string") return "/app/dashboard";
  const trimmed = path.trim();
  if (REDIRECT_PATH_ALIASES[trimmed] !== undefined) {
    return REDIRECT_PATH_ALIASES[trimmed];
  }
  // Only in-app paths. The server already sanitizes this at issue time
  // but the SPA guards again before handing the string to the router.
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return "/app/dashboard";
  }
  return trimmed;
}

function errorCodeFromDetail(detail) {
  if (detail && typeof detail === "object" && detail.error) return detail.error;
  if (typeof detail === "string") return detail;
  return "default";
}

// Exposed for unit testing. Keeping these as internal helpers so the
// default React export surface stays small.
export const __test = {
  pickLaunchCodeFromUrl,
  stripLaunchCodeFromUrl,
  normalizeRedirectPath,
  exchangeLaunchCode,
  inflightExchanges,
  ERROR_COPY,
};

export function LaunchCodeBootstrap({ children }) {
  // Compute phase lazily so a cold load with no `?launch_code=` skips all
  // bootstrap work entirely.
  const [phase, setPhase] = useState(() =>
    pickLaunchCodeFromUrl() ? "exchanging" : "idle",
  );
  const [errorCode, setErrorCode] = useState(null);
  // StrictMode runs effects twice. The ref dedupes the hydration-side
  // effects (setSession + navigate) so we never write twice from one
  // page load, even though the shared promise naturally dedupes the
  // network call.
  const hydratedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (phase !== "exchanging") return;
    const code = pickLaunchCodeFromUrl();
    if (!code) {
      setPhase("idle");
      return;
    }
    let alive = true;
    exchangeLaunchCode(code).then((result) => {
      if (!alive) return;
      if (!result.ok) {
        setErrorCode(errorCodeFromDetail(result.data?.detail));
        setPhase("error");
        return;
      }
      const session = result.data?.session || {};
      // Idempotent even if a prior mount already hydrated — same values.
      if (!hydratedRef.current) {
        hydratedRef.current = true;
        setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          user: session.user,
        });
        // Normalize `/dashboard` → `/app/dashboard` and refuse external
        // paths. Then let react-router actually do the navigation so the
        // route tree re-renders (not just the URL bar).
        const target = normalizeRedirectPath(result.data?.redirect_path);
        navigate(target, { replace: true });
      }
      setPhase("done");
    });
    return () => {
      // IMPORTANT: do NOT discard a successful result here. The second
      // StrictMode mount re-subscribes to the same shared promise and
      // keeps `alive=true` in its own closure. If this cleanup were to
      // flip a shared flag we would strand the hydration.
      alive = false;
    };
  }, [phase, navigate]);

  if (phase === "exchanging") {
    return (
      <div
        data-testid="launch-code-bootstrap-loading"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafafa",
          color: "#374151",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              margin: "0 auto 12px",
              border: "3px solid #e5e7eb",
              borderTopColor: "#111827",
              borderRadius: "50%",
              animation: "lcb-spin 0.8s linear infinite",
            }}
          />
          <div style={{ fontSize: 13 }}>Handing you off from PurePay…</div>
          <style>{`@keyframes lcb-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    const copy = ERROR_COPY[errorCode] || ERROR_COPY.default;
    return (
      <div
        data-testid="launch-code-bootstrap-error"
        data-launch-error={errorCode || "default"}
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#fafafa",
          color: "#111827",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "left" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", marginBottom: 8 }}>
            Quanta × PurePay
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 8px" }}>
            {copy.title}
          </h2>
          <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.5, margin: 0 }}>
            {copy.body}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default LaunchCodeBootstrap;
